<template>
  <NuxtLink
    v-if="internalHref"
    :to="localePath(internalHref)"
    class="prepeers-metric-card prepeers-metric-card--link">
    <div
      class="prepeers-metric-card__value"
      :class="valueClasses">
      {{ metric.Value }}
    </div>
    <div class="prepeers-metric-card__label">{{ metric.Name }}</div>
  </NuxtLink>
  <div
    v-else
    class="prepeers-metric-card">
    <div
      class="prepeers-metric-card__value"
      :class="valueClasses">
      {{ metric.Value }}
    </div>
    <div class="prepeers-metric-card__label">{{ metric.Name }}</div>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef, PropType } from 'vue'
import type { MetricObj } from '~/types/school'
import { resolveMetricLinkPath } from '~/utils/resolveMetricLinkPath'

const props = defineProps({
  metric: {
    type: Object as PropType<MetricObj>,
    required: true,
  },
  compactValueDesktop: {
    type: Boolean,
    default: false,
  },
  hasGradientValue: {
    type: Boolean,
    default: false,
  },
})

const localePath = useLocalePath()

const internalHref: ComputedRef<string | null> = computed((): string | null =>
  resolveMetricLinkPath(props.metric.Url),
)

const valueClasses: ComputedRef<Record<string, boolean>> = computed(
  (): Record<string, boolean> => ({
    'prepeers-metric-card__value--compact-desktop': props.compactValueDesktop,
    'prepeers-metric-card__value--gradient': props.hasGradientValue,
  }),
)
</script>

<style scoped>
.prepeers-metric-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-subtle);
  min-height: 120px;
  text-align: center;
  transition: all var(--duration-base) var(--ease-out);
  box-sizing: border-box;
}

.prepeers-metric-card:not(.prepeers-metric-card--link):hover {
  background-color: rgba(0, 0, 0, 0.4);
  transform: translateY(-2px);
}

/* Même lecture que UiProfessionCard : bordure + ombre au survol, fond de carte peu changé ; texte en dégradé bleu. */
.prepeers-metric-card--link {
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.prepeers-metric-card--link:hover {
  border-color: var(--accent-blue);
  box-shadow:
    var(--shadow-lg),
    0 0 20px rgba(3, 160, 241, 0.25);
  transform: translateY(-2px);
}

.prepeers-metric-card--link:hover .prepeers-metric-card__value,
.prepeers-metric-card--link:hover .prepeers-metric-card__label {
  background: var(--gradient-blue);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.prepeers-metric-card--link:focus-visible {
  outline: none;
  box-shadow:
    var(--shadow-lg),
    0 0 0 2px var(--accent-blue-lt);
}

.prepeers-metric-card__value {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  color: white;
  line-height: var(--leading-snug);
  margin-bottom: var(--space-3);
  letter-spacing: var(--tracking-tight);
  max-width: 100%;
  text-wrap: balance;
  transition: color var(--duration-fast) var(--ease-out);
}

.prepeers-metric-card__value--gradient {
  background: var(--gradient-blue);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.prepeers-metric-card__label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  line-height: var(--leading-tight);
  transition: color var(--duration-fast) var(--ease-out);
}

@media (min-width: 1025px) {
  .prepeers-metric-card__value.prepeers-metric-card__value--compact-desktop {
    font-size: clamp(1.625rem, 3.35vw, 2rem);
  }
}

@media (min-width: 768px) and (max-width: 1024px) {
  .prepeers-metric-card__value.prepeers-metric-card__value--compact-desktop {
    font-size: clamp(1.5rem, 3.4vw, 2.05rem);
  }
}

@media (max-width: 1024px) {
  .prepeers-metric-card {
    min-height: 112px;
    padding: var(--space-5);
  }

  .prepeers-metric-card__value {
    font-size: clamp(1.75rem, 4vw, 2.5rem);
  }
}

@media (max-width: 768px) {
  .prepeers-metric-card {
    min-height: 104px;
    padding: var(--space-4);
  }

  .prepeers-metric-card__value {
    font-size: clamp(1.5rem, 3vw, 2rem);
  }

  .prepeers-metric-card__label {
    font-size: var(--text-xs);
  }
}

@media (max-width: 640px) {
  .prepeers-metric-card {
    min-height: 96px;
    padding: var(--space-3);
  }

  .prepeers-metric-card__value {
    font-size: var(--text-3xl);
  }

  .prepeers-metric-card__label {
    font-size: var(--text-xs);
  }
}
</style>
